import { useQuery } from "@tanstack/react-query";
import { useGetIdentity, type Identifier } from "ra-core";

import type { ImageTriageResponse } from "../../types";
import {
  DashboardRequestError,
  dashboardRequest,
} from "../../providers/aws/client";

export async function fetchImageTriage(
  sessionId: Identifier,
  signal?: AbortSignal,
): Promise<ImageTriageResponse> {
  return dashboardRequest<ImageTriageResponse>(
    `/video-sessions/${encodeURIComponent(String(sessionId))}/image-triage?limit=250`,
    signal ? { signal } : {},
  );
}

function shouldRetry(failureCount: number, error: Error): boolean {
  if (
    error instanceof DashboardRequestError &&
    [401, 403, 404].includes(error.status)
  ) {
    return false;
  }
  return failureCount < 2;
}

export function useImageTriage(sessionId?: Identifier | null) {
  const { identity } = useGetIdentity();
  return useQuery({
    queryKey: [
      "image-triage",
      sessionId == null ? "no-session" : String(sessionId),
      identity?.id == null ? "no-identity" : String(identity.id),
    ],
    queryFn: ({ signal }) => fetchImageTriage(sessionId!, signal),
    enabled: sessionId != null && identity?.id != null,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchInterval: (query) => {
      const state = query.state.data?.data.state;
      return state === "pending" || state === "partial_projection"
        ? 5_000
        : false;
    },
    retry: shouldRetry,
  });
}
